
import { useState } from 'react';
import { 
  Sheet, 
  SheetClose, 
  SheetContent, 
  SheetDescription, 
  SheetFooter, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger 
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Filter, Zap, DollarSign, Clock, Home } from 'lucide-react';

interface StationFiltersProps {
  onFilterChange: (filters: FilterOptions) => void;
}

export interface FilterOptions {
  price: [number, number];
  powerOutput: [number, number];
  chargerTypes: string[];
  homeOnly: boolean;
  availableOnly: boolean;
}

const defaultFilters: FilterOptions = {
  price: [0, 1],
  powerOutput: [0, 350],
  chargerTypes: ['standard', 'fast', 'ultra-fast'],
  homeOnly: false,
  availableOnly: false,
};

const StationFilters = ({ onFilterChange }: StationFiltersProps) => {
  const [filters, setFilters] = useState<FilterOptions>(defaultFilters);

  const handleReset = () => {
    setFilters(defaultFilters);
    onFilterChange(defaultFilters);
  };

  const handleApply = () => {
    onFilterChange(filters);
  };

  const updateFilter = <K extends keyof FilterOptions>(
    key: K,
    value: FilterOptions[K]
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          <span>Filters</span>
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Filter Charging Stations</SheetTitle>
          <SheetDescription>
            Customize your search to find the perfect charging station.
          </SheetDescription>
        </SheetHeader>

        <div className="py-6 space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <Label>Price per kWh</Label>
              </div>
              <span className="text-sm font-medium">
                ${filters.price[0].toFixed(2)} - ${filters.price[1].toFixed(2)}
              </span>
            </div>
            <Slider
              min={0}
              max={2}
              step={0.05}
              value={filters.price}
              onValueChange={(value) => updateFilter('price', value as [number, number])}
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-muted-foreground" />
                <Label>Power Output (kW)</Label>
              </div>
              <span className="text-sm font-medium">
                {filters.powerOutput[0]} - {filters.powerOutput[1]} kW
              </span>
            </div>
            <Slider
              min={0}
              max={350}
              step={10}
              value={filters.powerOutput}
              onValueChange={(value) => updateFilter('powerOutput', value as [number, number])}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-base">Charger Types</Label>
            <div className="space-y-2">
              {[
                { id: 'standard', label: 'Standard (Level 2)' },
                { id: 'fast', label: 'Fast Charging' },
                { id: 'ultra-fast', label: 'Ultra-Fast Charging' },
              ].map((type) => (
                <div key={type.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={type.id}
                    checked={filters.chargerTypes.includes(type.id)}
                    onCheckedChange={(checked) => {
                      const newTypes = checked
                        ? [...filters.chargerTypes, type.id]
                        : filters.chargerTypes.filter((t) => t !== type.id);
                      updateFilter('chargerTypes', newTypes);
                    }}
                  />
                  <label
                    htmlFor={type.id}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {type.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-base">Additional Filters</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="availableOnly"
                  checked={filters.availableOnly}
                  onCheckedChange={(checked) => 
                    updateFilter('availableOnly', !!checked)
                  }
                />
                <label
                  htmlFor="availableOnly"
                  className="flex items-center text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  <Clock className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                  Available Now
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="homeOnly"
                  checked={filters.homeOnly}
                  onCheckedChange={(checked) => 
                    updateFilter('homeOnly', !!checked)
                  }
                />
                <label
                  htmlFor="homeOnly"
                  className="flex items-center text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  <Home className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                  Home Chargers Only
                </label>
              </div>
            </div>
          </div>
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={handleReset}>
            Reset
          </Button>
          <SheetClose asChild>
            <Button onClick={handleApply}>Apply Filters</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default StationFilters;
